import { isEmpty, type DashboardFilter, type RoleId, type TeamId, type UserId } from '@terros/common'

/** How many scope avatars a widget card shows before collapsing into "+N". */
export const MAX_WIDGET_SCOPE_AVATARS = 3

/**
 * One entity a widget's data is scoped to. `currentUser` / `currentTeam` are
 * resolved against the viewer at render time rather than naming an id, because
 * those filters mean "whoever is looking".
 */
export type WidgetScopeRef =
  | { kind: 'currentUser' }
  | { kind: 'currentTeam' }
  | { kind: 'user'; userId: UserId }
  | { kind: 'team'; teamId: TeamId }
  | { kind: 'role'; roleId: RoleId }

export type WidgetScope = {
  refs: WidgetScopeRef[]
  /** How many more entities the filter targets beyond the ones shown. */
  overflow: number
}

const EMPTY_SCOPE: WidgetScope = { refs: [], overflow: 0 }

/**
 * The entities a widget's data is scoped to, for the card's facepile. A
 * company-wide widget (no filter) resolves to nothing, which is what hides the
 * facepile entirely.
 */
export function getWidgetScopeRefs(filter: DashboardFilter | undefined): WidgetScopeRef[] {
  if (!filter) return []

  switch (filter.type) {
    case 'current_user':
      return [{ kind: 'currentUser' }]
    case 'current_team':
      return [{ kind: 'currentTeam' }]
    case 'fixed':
      return [
        ...(filter.filter.teams ?? []).map((teamId): WidgetScopeRef => ({ kind: 'team', teamId })),
        ...(filter.filter.users ?? []).map((userId): WidgetScopeRef => ({ kind: 'user', userId })),
        ...(filter.filter.roles ?? []).map((roleId): WidgetScopeRef => ({ kind: 'role', roleId })),
      ]
  }
}

/** Caps the refs at what the card renders, reporting the remainder as overflow. */
export function getWidgetScope(filter: DashboardFilter | undefined): WidgetScope {
  const refs = getWidgetScopeRefs(filter)
  if (isEmpty(refs)) return EMPTY_SCOPE

  return {
    refs: refs.slice(0, MAX_WIDGET_SCOPE_AVATARS),
    overflow: Math.max(0, refs.length - MAX_WIDGET_SCOPE_AVATARS),
  }
}

export function toWidgetScopeRefKey(ref: WidgetScopeRef): string {
  switch (ref.kind) {
    case 'currentUser':
      return 'currentUser'
    case 'currentTeam':
      return 'currentTeam'
    case 'user':
      return ref.userId
    case 'team':
      return ref.teamId
    case 'role':
      return ref.roleId
  }
}
